import { useEffect } from 'react';
import { FCM } from '@capacitor-community/fcm';
import { ActionPerformed, PushNotifications, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { useProjtechNavigate } from './useNavigate';
import { RoutesData } from '@data';
import { openDialog } from '@projtech-package/react-components/lib';
import { ISettingsAtom } from '@store/settings';
import { queryClient, QueryKeys } from '@utils';

export const useFCM = (audioRef: React.MutableRefObject<null>, notificationSettings: ISettingsAtom) => {
	const { router } = useProjtechNavigate();
	// const notificationSettings = useAtomValue(SettingsAtom);
	useEffect(() => {
		if (Capacitor.getPlatform() === 'web') return;
		// 🔹 Push-Berechtigungen anfordern
		PushNotifications.requestPermissions().then((permission) => {
			if (permission.receive === 'granted') {
				PushNotifications.register();
				const topics = [
					'all',
					...Object.keys(notificationSettings.notifications).filter(
						(key) => notificationSettings.notifications[key as keyof typeof notificationSettings.notifications],
					),
					...Object.keys(notificationSettings.prayerTimesSettings).filter(
						(key) =>
							notificationSettings.prayerTimesSettings[key as keyof typeof notificationSettings.prayerTimesSettings],
					),
				];
				topics.forEach((topic) => {
					FCM.subscribeTo({ topic })
						.then(() => console.log(`✅ Abonniert: ${topic}`))
						.catch((err) => console.error('❌ Fehler beim Abonnieren', err));
				});
			}
		});

		PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
			const type = notification.data.type;
			const id = notification.data.id;
			if (notification?.data.type === 'prayerTime') {
				// @ts-ignore
				// audioRef?.current?.play();
			}
			if (type?.startsWith('STUDENT_') || type?.startsWith('TEACHER_')) {
				if (typeof id === 'string' && id?.startsWith('HOMEWORK_')) {
					queryClient.invalidateQueries(QueryKeys.PUSH_NOTIFICATIONS);
				}
			}
		});
		PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
			const id: string | number = notification.notification.data.id;
			const type: string = notification.notification.data.type;

			switch (type) {
				case 'prayerTime':
					// @ts-ignore
					// audioRef?.current?.play();
					return router(RoutesData['prayer-times'].route, RoutesData['prayer-times'].title);
				case 'notification':
					queryClient.invalidateQueries(QueryKeys.PUSH_NOTIFICATIONS);
					return openDialog('open-notification-dialog');
				case 'lesson':
					if (id) {
						return router(
							`${RoutesData.detail.route.replace(':detailId', id.toString()) + ''}`,
							RoutesData.detail.title,
						);
					}
					return router(RoutesData['week-schedule'].route, RoutesData['week-schedule'].title);
				case 'activity':
					if (id) {
						return router(
							`${RoutesData.detail.route.replace(':detailId', id.toString()) + ''}`,
							RoutesData.detail.title,
						);
					}
					return router(`${RoutesData.activities.route}`, RoutesData['activities'].title);
				case 'quran':
					return router(RoutesData['quran'].route, RoutesData['quran'].title);
				case 'duaa':
					if (id) {
						const hasMaathurat = id === 1 || id === 2 || id === '1' || id === '2';
						return router(
							`${
								RoutesData['content-detail'].route.replace(':contentId', id.toString()) +
								'?type=prayer' +
								(hasMaathurat ? '&playingAudio=true' : '')
							}`,
							RoutesData.detail.title,
						);
					}
					return router(RoutesData['duaa'].route, RoutesData['duaa'].title);
				default: {
					const isUser = type?.startsWith('STUDENT_') || type?.startsWith('TEACHER_');

					if (isUser && typeof id === 'string' && id.startsWith('HOMEWORK_')) {
						try {
							const payload = decodeURIComponent(id.slice('HOMEWORK_'.length));
							const hw = JSON.parse(payload) as {
								subscriberId?: number | string;
								date?: string;
								subscribeId?: number | string;
							};

							const subscriberId = hw.subscriberId ?? hw.subscribeId;
							if (subscriberId != null) {
								const sid = String(subscriberId);
								const q = hw.date ? `?date=${encodeURIComponent(hw.date)}` : '';

								return router(
									`${RoutesData.student.children.subscriber.children.single.route.replace(':id', sid)}${q}`,
								);
							}
						} catch (e) {
							console.error('Invalid HOMEWORK payload', e);
						}
					}
				}
			}
		});
	}, []);
};

export const subscribeToTopic = async (topic: string) => {
	if (Capacitor.getPlatform() === 'web') return;
	try {
		await FCM.subscribeTo({ topic: 'korani' + '_' + topic });
		console.log(`✅ Erfolgreich abonniert: ${'korani' + '_' + topic}`);
	} catch (err) {
		console.error('❌ Fehler beim Abonnieren', err);
	}
};

export const unsubscribeFromTopic = async (topic: string) => {
	if (Capacitor.getPlatform() === 'web') return;
	try {
		await FCM.unsubscribeFrom({ topic: 'korani' + '_' + topic });
		console.log(`🚫 Erfolgreich abgemeldet von: ${'korani' + '_' + topic}`);
	} catch (err) {
		console.error('❌ Fehler beim Abmelden', err);
	}
};
